/**
 * Mock AI service for workflow generation.
 * Returns canned responses to simulate AI-generated workflows.
 * Used for development and testing until AWS Bedrock is wired.
 */

const MOCK_TEMPLATES = {
  safety: {
    id: 'wf-ai-safety',
    name: 'Daily Safety Checklist',
    version: 1,
    domain: 'safety',
    cadence: 'daily',
    swimlanes: [
      { id: 'lane-rn', label: 'Registered Nurse', role: 'triage_rn' },
      { id: 'lane-mgr', label: 'Office Manager', role: 'com' },
    ],
    machine: {
      id: 'daily-safety',
      initial: 'fire_extinguisher_check',
      context: { findings: [], completedBy: {} },
      states: {
        fire_extinguisher_check: {
          meta: { nodeType: 'task', swimlane: 'lane-rn', label: 'Fire Extinguisher Check', completionType: 'checkbox_linked', estimatedMinutes: 10 },
          on: { COMPLETE: { target: 'emergency_exit_check' }, SKIP: { target: 'emergency_exit_check' } },
        },
        emergency_exit_check: {
          meta: { nodeType: 'task', swimlane: 'lane-rn', label: 'Emergency Exit Inspection', completionType: 'checkbox', estimatedMinutes: 5 },
          on: { COMPLETE: { target: 'first_aid_check' } },
        },
        first_aid_check: {
          meta: { nodeType: 'task', swimlane: 'lane-rn', label: 'First Aid Kit Inventory', completionType: 'checkbox_note', estimatedMinutes: 15 },
          on: { COMPLETE: { target: 'safety_review' } },
        },
        safety_review: {
          meta: { nodeType: 'gate', swimlane: 'lane-mgr', label: 'Any findings?', options: ['Yes', 'No'] },
          always: [
            { target: 'corrective_action', guard: 'hasFindings' },
            { target: 'sign_off' },
          ],
        },
        corrective_action: {
          meta: { nodeType: 'task', swimlane: 'lane-mgr', label: 'Document Corrective Action', completionType: 'checkbox_note', estimatedMinutes: 20 },
          on: { COMPLETE: { target: 'sign_off' } },
        },
        sign_off: {
          meta: { nodeType: 'task', swimlane: 'lane-mgr', label: 'Manager Sign-Off', completionType: 'checkbox', estimatedMinutes: 5 },
          on: { COMPLETE: { target: 'done' } },
        },
        done: { type: 'final', meta: { nodeType: 'end', label: 'Complete' } },
      },
    },
    triggers: [],
    guards: {
      hasFindings: { type: 'context', path: 'findings.length', operator: '>', value: 0 },
    },
    actions: {},
  },

  quality: {
    id: 'wf-ai-quality',
    name: 'Monthly Quality Audit',
    version: 1,
    domain: 'quality',
    cadence: 'monthly',
    swimlanes: [
      { id: 'lane-rn', label: 'Charge Nurse', role: 'charge_nurse' },
      { id: 'lane-mgr', label: 'Clinic Ops Manager', role: 'com' },
    ],
    machine: {
      id: 'monthly-quality',
      initial: 'chart_review',
      context: { findings: [], completedBy: {} },
      states: {
        chart_review: {
          meta: { nodeType: 'task', swimlane: 'lane-rn', label: 'Patient Chart Review', completionType: 'checkbox_linked', estimatedMinutes: 45 },
          on: { COMPLETE: { target: 'parallel_checks' } },
        },
        parallel_checks: {
          type: 'parallel',
          meta: { nodeType: 'parallel', label: 'Parallel Quality Checks' },
          states: {
            medication_track: {
              initial: 'med_reconciliation',
              states: {
                med_reconciliation: {
                  meta: { nodeType: 'task', swimlane: 'lane-rn', label: 'Medication Reconciliation', completionType: 'checkbox_note', estimatedMinutes: 30 },
                  on: { COMPLETE: { target: 'med_done' } },
                },
                med_done: { type: 'final', meta: { nodeType: 'end' } },
              },
            },
            infection_track: {
              initial: 'infection_control',
              states: {
                infection_control: {
                  meta: { nodeType: 'task', swimlane: 'lane-rn', label: 'Infection Control Audit', completionType: 'checkbox_linked', estimatedMinutes: 25 },
                  on: { COMPLETE: { target: 'infection_done' } },
                },
                infection_done: { type: 'final', meta: { nodeType: 'end' } },
              },
            },
          },
          onDone: 'quality_gate',
        },
        quality_gate: {
          meta: { nodeType: 'gate', swimlane: 'lane-mgr', label: 'Pass or Fail?', options: ['Pass', 'Fail'] },
          always: [
            { target: 'remediation', guard: 'hasFindings' },
            { target: 'final_report' },
          ],
        },
        remediation: {
          meta: { nodeType: 'task', swimlane: 'lane-mgr', label: 'Create Remediation Plan', completionType: 'checkbox_note', estimatedMinutes: 30 },
          on: { COMPLETE: { target: 'final_report' } },
        },
        final_report: {
          meta: { nodeType: 'task', swimlane: 'lane-mgr', label: 'Final Report & Sign-Off', completionType: 'checkbox_linked', estimatedMinutes: 20 },
          on: { COMPLETE: { target: 'done' } },
        },
        done: { type: 'final', meta: { nodeType: 'end', label: 'Audit Complete' } },
      },
    },
    triggers: [
      { type: 'cron', expression: '0 9 1 * *', action: 'START' },
    ],
    guards: {
      hasFindings: { type: 'context', path: 'findings.length', operator: '>', value: 0 },
    },
    actions: {},
  },

  compliance: {
    id: 'wf-ai-compliance',
    name: 'OSHA Compliance Walkthrough',
    version: 1,
    domain: 'compliance',
    cadence: 'quarterly',
    regulatorySource: 'osha',
    swimlanes: [
      { id: 'lane-rn', label: 'Safety Officer', role: 'charge_nurse' },
      { id: 'lane-mgr', label: 'Practice Manager', role: 'com' },
    ],
    machine: {
      id: 'osha-compliance',
      initial: 'hazard_assessment',
      context: { findings: [], completedBy: {} },
      states: {
        hazard_assessment: {
          meta: { nodeType: 'task', swimlane: 'lane-rn', label: 'Workplace Hazard Assessment', completionType: 'checkbox_linked', estimatedMinutes: 60 },
          on: { COMPLETE: { target: 'ppe_check' } },
          after: { 172800000: { target: 'hazard_escalation' } },
        },
        hazard_escalation: {
          meta: { nodeType: 'timer', label: '48h SLA Breach', duration: 172800000 },
          on: { COMPLETE: { target: 'ppe_check' } },
        },
        ppe_check: {
          meta: { nodeType: 'task', swimlane: 'lane-rn', label: 'PPE Inventory & Compliance', completionType: 'checkbox_note', estimatedMinutes: 30 },
          on: { COMPLETE: { target: 'training_verification' } },
        },
        training_verification: {
          meta: { nodeType: 'task', swimlane: 'lane-mgr', label: 'Training Records Verification', completionType: 'checkbox_linked', estimatedMinutes: 45 },
          on: { COMPLETE: { target: 'compliance_gate' } },
        },
        compliance_gate: {
          meta: { nodeType: 'gate', swimlane: 'lane-mgr', label: 'Compliant?', options: ['Yes', 'No'] },
          always: [
            { target: 'action_plan', guard: 'hasFindings' },
            { target: 'certification' },
          ],
        },
        action_plan: {
          meta: { nodeType: 'task', swimlane: 'lane-mgr', label: 'Corrective Action Plan', completionType: 'checkbox_note', estimatedMinutes: 60 },
          on: { COMPLETE: { target: 'certification' } },
        },
        certification: {
          meta: { nodeType: 'task', swimlane: 'lane-mgr', label: 'Sign Compliance Certificate', completionType: 'checkbox_linked', estimatedMinutes: 10 },
          on: { COMPLETE: { target: 'done' } },
        },
        done: { type: 'final', meta: { nodeType: 'end', label: 'Certified' } },
      },
    },
    triggers: [
      { type: 'cron', expression: '0 9 1 1,4,7,10 *', action: 'START' },
      { type: 'sla', stateId: 'hazard_assessment', deadline: 'P2D', escalateTo: 'manager' },
    ],
    guards: {
      hasFindings: { type: 'context', path: 'findings.length', operator: '>', value: 0 },
    },
    actions: {},
  },
};

/**
 * Simulate latency.
 */
function delay(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Pick the best matching template based on keywords in the prompt.
 */
function matchTemplate(prompt) {
  const p = prompt.toLowerCase();
  if (p.includes('safety') || p.includes('fire') || p.includes('emergency')) return 'safety';
  if (p.includes('quality') || p.includes('audit') || p.includes('chart')) return 'quality';
  if (p.includes('compliance') || p.includes('osha') || p.includes('regulatory')) return 'compliance';
  // Default to safety
  return 'safety';
}

export class MockWorkflowAIService {
  /**
   * Generate a new workflow from a natural language description.
   * @param {string} prompt - User's description
   * @param {Object} metadata - { name, domain, cadence, regulatorySource }
   * @returns {Promise<Object>} - v2 workflow schema
   */
  async generateWorkflow(prompt, metadata = {}) {
    await delay(1200 + Math.random() * 800); // Simulate 1.2-2s latency

    const templateKey = matchTemplate(prompt);
    const template = { ...MOCK_TEMPLATES[templateKey] };

    // Override with user-provided metadata
    const id = `wf-${Date.now()}`;
    return {
      ...template,
      id,
      name: metadata.name || template.name,
      domain: metadata.domain || template.domain,
      cadence: metadata.cadence || template.cadence,
      regulatorySource: metadata.regulatorySource || template.regulatorySource,
      machine: {
        ...template.machine,
        id,
      },
    };
  }

  /**
   * Modify an existing workflow based on a natural language instruction.
   * @param {string} prompt - User's modification request
   * @param {Object} currentSchema - Current v2 workflow schema
   * @returns {Promise<{schema: Object, summary: string}>}
   */
  async modifyWorkflow(prompt, currentSchema) {
    await delay(1000 + Math.random() * 600);

    const p = prompt.toLowerCase();

    // Simulate adding a task node
    if (p.includes('add') && (p.includes('task') || p.includes('step'))) {
      const newStateId = `task_${Date.now()}`;
      const modified = { ...currentSchema };
      const machine = { ...modified.machine, states: { ...modified.machine.states } };

      // Find the last non-final state
      const stateEntries = Object.entries(machine.states);
      const lastNonFinal = stateEntries.filter(([, s]) => s.type !== 'final').pop();
      const finalState = stateEntries.find(([, s]) => s.type === 'final');

      if (lastNonFinal && finalState) {
        // Insert new task before final
        machine.states[newStateId] = {
          meta: { nodeType: 'task', label: 'New Task (AI Generated)', completionType: 'checkbox', estimatedMinutes: 10 },
          on: { COMPLETE: { target: finalState[0] } },
        };
        // Redirect last non-final to new task
        if (machine.states[lastNonFinal[0]].on?.COMPLETE) {
          machine.states[lastNonFinal[0]] = {
            ...machine.states[lastNonFinal[0]],
            on: { ...machine.states[lastNonFinal[0]].on, COMPLETE: { target: newStateId } },
          };
        }
      }

      modified.machine = machine;
      return {
        schema: modified,
        summary: `Added a new task step "${newStateId}" before the finish. The previous last step now transitions to this new task.`,
      };
    }

    // Simulate adding an SLA timer
    if (p.includes('sla') || p.includes('timer') || p.includes('deadline')) {
      const modified = { ...currentSchema };
      const hours = parseInt(p.match(/(\d+)\s*h/)?.[1] || '48', 10);
      modified.triggers = [
        ...(modified.triggers || []),
        { type: 'sla', stateId: modified.machine.initial, deadline: `PT${hours}H`, escalateTo: 'manager' },
      ];
      return {
        schema: modified,
        summary: `Added a ${hours}-hour SLA timer on the first step "${modified.machine.initial}". If not completed in time, the manager will be notified.`,
      };
    }

    // Simulate adding a swimlane
    if (p.includes('swimlane') || p.includes('role') || p.includes('lane')) {
      const modified = { ...currentSchema };
      const roleName = p.match(/for\s+(\w+)/i)?.[1] || 'nurse';
      const newLane = {
        id: `lane-${roleName.toLowerCase()}`,
        label: roleName.charAt(0).toUpperCase() + roleName.slice(1),
        role: roleName.toLowerCase(),
      };
      modified.swimlanes = [...(modified.swimlanes || []), newLane];
      return {
        schema: modified,
        summary: `Added a swimlane for "${newLane.label}" (role: ${newLane.role}). Assign tasks to this lane from the node config panel.`,
      };
    }

    // Default: return unchanged with a message
    return {
      schema: currentSchema,
      summary: "I understand your request but I'm currently in mock mode. When connected to AWS Bedrock, I'll be able to make more sophisticated modifications. For now, try: 'add a task', 'add a 48h SLA', or 'add swimlane for nurse'.",
    };
  }

  /**
   * Explain the current workflow execution status.
   * @param {Object} currentSnapshot - XState actor snapshot
   * @returns {Promise<string>}
   */
  async explainStatus(currentSnapshot) {
    await delay(500);
    if (!currentSnapshot) return 'No workflow is currently running.';

    const value = typeof currentSnapshot.value === 'string'
      ? currentSnapshot.value
      : JSON.stringify(currentSnapshot.value);

    return `The workflow is currently at: ${value}. Complete the active step to move forward.`;
  }

  /**
   * Suggest next actions based on current state.
   * @param {Object} currentSnapshot
   * @returns {Promise<string[]>}
   */
  async suggestNextActions(currentSnapshot) {
    await delay(300);
    return [
      'Complete the current task',
      'Skip if not applicable',
      'Add a note before completing',
    ];
  }
}
